import React, { useEffect, useState } from "react";
import * as _ from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { Select, Form, List, Message } from "semantic-ui-react";
import { editActions } from "../../actions/editActions";

interface ModifyDoorParameterBindingsProps {
  mode: string;
  permission: boolean;
}

const ModifyDoorParameterBindings: React.FC<ModifyDoorParameterBindingsProps> = ({
  mode,
  permission
}) => {
  const dispatch = useDispatch();
  const pdApi = useSelector((state: State.Root) => state.pdApi);
  const domains = useSelector((state: State.Root) => state.pdApi.domains);

  const [domainId, setDomainId] = useState(-1);
  const [doorVersionId, setDoorVersionId] = useState(-1);
  const [versionOptions, setVersionOptions] = useState<{ key: number; text: string; value: number }[]>([]);

  // Initialise domain from first parameter's domain on mount (mirrors Escalator componentDidMount)
  useEffect(() => {
    if (pdApi.doorParameters.length > 0) {
      setDomainId(pdApi.doorParameters[0].domain.id);
    }
  }, [pdApi.doorParameters]);

  // Re-compute version options whenever domain changes (mirrors Escalator componentDidUpdate + updateVersionOptions)
  useEffect(() => {
    if (domainId > 0 && pdApi.DoorVersions) {
      const filtered = pdApi.DoorVersions.filter(dv => dv.domain.id === domainId);
      if (filtered.length > 0) {
        const mappedOptions = filtered.map(dv => ({
          key: dv.id ?? -1,
          text: `${dv.number}.${domains[dv.domain.id]?.enumName} - ${dv.versionName}`,
          value: dv.id ?? -1
        }));
        const sorted = _.sortBy(mappedOptions, ["text"]).reverse();
        setVersionOptions(sorted);
        setDoorVersionId(sorted[0].value ?? -1);
      } else {
        setVersionOptions([]);
        setDoorVersionId(-1);
      }
    } else {
      setVersionOptions([]);
      setDoorVersionId(-1);
    }
  }, [domainId, pdApi.DoorVersions, domains]);

  // Build domain options only from domains that have existing door parameters (mirrors Escalator domainOptions)
  const usedDomainIds = _.uniq(pdApi.doorParameters.map(p => p.domain.id));
  const domainOptions = usedDomainIds
    .sort((a, b) => (domains[a]?.enumValue ?? 0) - (domains[b]?.enumValue ?? 0))
    .map(usedDomainId => ({
      key: usedDomainId,
      text: domains[usedDomainId]?.enumName + " [" + domains[usedDomainId]?.enumValue + "]",
      value: usedDomainId
    }));

  // Get parameters for the selected domain filtered by binding status (mirrors Escalator selectedParameters)
  let selectedParameters: Api.DoorParameter[] = [];
  if (mode === "Add") {
    // Parameters in the domain that do NOT have a binding to the selected version
    selectedParameters = pdApi.doorParameters.filter(
      p =>
        p.domain.id === domainId &&
        !p.doorParameterBindings.find(b => b.doorVersion.id === doorVersionId)
    );
  } else {
    // Parameters in the domain that DO have a binding to the selected version
    selectedParameters = pdApi.doorParameters.filter(
      p =>
        p.domain.id === domainId &&
        !!p.doorParameterBindings.find(b => b.doorVersion.id === doorVersionId)
    );
  }

  const selectedParametersCount = selectedParameters.length;
  const infoText =
    mode === "Add"
      ? "Add selected binding to the following parameters"
      : "Remove selected binding from the following parameters";

  const selectedParametersNames = selectedParameters.map(
    p => `${p.enumName} (code = ${p.code})`
  );

  const createBindings = () => {
    const parametersId = selectedParameters.map(p => p.id) as number[];
    const bindings: Api.DoorBindings = {
      targetVersionId: doorVersionId,
      ParameterIdList: parametersId,
      domainId
    };
    dispatch(editActions.updateDoorBindings(mode, bindings));
  };

  return (
    <>
      {mode === "Add" ? (
        <Message
          info={true}
          header="Add version bindings to parameters"
          list={[
            "Select the binding which will be added to all parameters in the selected domain",
            "The list shows parameters to which the binding will be added",
            "Start adding by clicking 'Add binding'"
          ]}
        />
      ) : (
        <Message
          info={true}
          header="Remove version bindings from parameters"
          list={[
            "Select the binding which will be removed from all parameters in the selected domain",
            "The list shows parameters from which the binding will be removed",
            "Start removing by clicking 'Remove binding'"
          ]}
        />
      )}
      <Form.Group>
        <Form>
          <div className="uidropdown">
            <Form.Field
              id="doorDomainDropdown"
              control={Select}
              label="Domain"
              options={domainOptions}
              search={true}
              value={domainId}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(e: Event, { value }: any) => {
                setDomainId(value);
                setDoorVersionId(-1); // Reset version immediately to prevent stale cross-domain filter
              }}
              disabled={permission}
            />
            <Form.Field
              id="doorVersionsDropdown"
              control={Select}
              label="Version"
              options={versionOptions}
              value={doorVersionId}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(e: Event, { value }: any) => setDoorVersionId(value)}
              disabled={permission}
            />
          </div>
        </Form>
      </Form.Group>
      <Form.Group className="bindingParameters">
        {domainId > 0 && doorVersionId > 0 && selectedParametersCount > 0 ? (
          <>
            <label>{`${infoText} (${selectedParametersCount}):`}</label>
            <div>
              <List items={selectedParametersNames} />
            </div>
          </>
        ) : doorVersionId <= 0 ? (
          <label>No version is available under the selected domain.</label>
        ) : (
          <label>Selected binding and domain do not match any parameters.</label>
        )}
      </Form.Group>
      <>
        {mode === "Add" ? (
          <button
            id="addDoorBindingsButton"
            disabled={selectedParametersCount === 0 || permission || domainId <= 0 || doorVersionId <= 0}
            onClick={createBindings}
            className="bindingsActionBtn"
          >
            Add bindings
          </button>
        ) : (
          <button
            id="removeDoorBindingsButton"
            disabled={selectedParametersCount === 0 || permission || domainId <= 0 || doorVersionId <= 0}
            onClick={createBindings}
            className="bindingsActionBtn"
          >
            Remove bindings
          </button>
        )}
      </>
    </>
  );
};

export default ModifyDoorParameterBindings;
